import { Color, Entity, Matrix4, ParticleSystem, Cartesian2, ConeEmitter, TranslationRotationScale, CallbackProperty, } from "cesium";
import { useRef, useEffect, useMemo } from "react";
import { useCesium } from "../Contexts/Widget3DContext";
import { degToRad, thrusterRotation } from "./utils";
const PARTICLE_STARTING_COLOR = Color.WHITE.withAlpha(1.0);
const PARTICLE_ENDING_COLOR = Color.WHITE.withAlpha(1.0);
const CONE_EMMITER_APERTURE = 15;
const EMISSION_RATE = 300.0;
const MINIMUM_PARTICLE_LIFE = 0.003;
const MAXIMUM_PARTICLE_LIFE = 0.003;
const MINIMUM_SPEED = 0.1;
const MAXIMUM_SPEED = 0.2;
const START_SCALE = 3.0;
const END_SCALE = 5.0;
const PARTICLE_SIZE = 10.0;
const LIFE_TIME = 0.003;
export const Thruster = ({ id, parentName, positionOffset, availability, }) => {
    const { isViewerLoaded, addEntity, removeEntity, addPrimitive, removePrimitive, getEntityReferenceProperty, addClockEvent, } = useCesium();
    const unsubscribeThrusterPSUpdater = useRef(null);
    const thrusterEntity = useRef(null);
    const thrusterPSPrimitive = useRef(null);
    const computeEmitterModelMatrix = () => {
        const trs = new TranslationRotationScale();
        trs.translation = positionOffset;
        trs.rotation = thrusterRotation;
        return Matrix4.fromTranslationRotationScale(trs, new Matrix4());
    };
    const createThrusterPS = (smoke) => {
        const thrusterPS = new ParticleSystem({
            image: smoke,
            startColor: PARTICLE_STARTING_COLOR,
            endColor: PARTICLE_ENDING_COLOR,
            startScale: START_SCALE,
            endScale: END_SCALE,
            minimumParticleLife: MINIMUM_PARTICLE_LIFE,
            maximumParticleLife: MAXIMUM_PARTICLE_LIFE,
            minimumSpeed: MINIMUM_SPEED,
            maximumSpeed: MAXIMUM_SPEED,
            imageSize: new Cartesian2(PARTICLE_SIZE, PARTICLE_SIZE),
            emissionRate: EMISSION_RATE,
            lifetime: LIFE_TIME,
            emitter: new ConeEmitter(degToRad(CONE_EMMITER_APERTURE)),
            emitterModelMatrix: computeEmitterModelMatrix(),
        });
        return addPrimitive(thrusterPS);
    };
    const updateThrusterPS = new CallbackProperty((time) => {
        var _a, _b, _c;
        const mm = (_a = thrusterEntity === null || thrusterEntity === void 0 ? void 0 : thrusterEntity.current) === null || _a === void 0 ? void 0 : _a.computeModelMatrix(time, new Matrix4());
        if ((thrusterPSPrimitive === null || thrusterPSPrimitive === void 0 ? void 0 : thrusterPSPrimitive.current) && mm) {
            thrusterPSPrimitive.current.modelMatrix = mm;
            const showThruster = (_c = (_b = thrusterEntity === null || thrusterEntity === void 0 ? void 0 : thrusterEntity.current) === null || _b === void 0 ? void 0 : _b.availability) === null || _c === void 0 ? void 0 : _c.contains(time);
            thrusterPSPrimitive.current.show = showThruster;
        }
        else {
            thrusterPSPrimitive.current.show = false;
        }
    }, false);
    const getThrusterAvailability = useMemo(() => {
        var _a;
        if ((_a = thrusterEntity === null || thrusterEntity === void 0 ? void 0 : thrusterEntity.current) === null || _a === void 0 ? void 0 : _a.availability) {
            thrusterEntity.current.availability = availability;
        }
        return availability;
    }, [availability]);
    const addThruster = () => {
        const newThrusterEntity = new Entity({
            id: `${parentName} thruster ${id}`,
            name: `${parentName} thruster ${id}`,
            position: getEntityReferenceProperty(parentName, "position"),
            orientation: getEntityReferenceProperty(parentName, "orientation"),
            availability: availability ? getThrusterAvailability : undefined,
        });
        return addEntity(newThrusterEntity);
    };
    useEffect(() => {
        const readSmokeAndInitialize = async () => {
            let smoke;
            try {
                smoke = await require("../../public/static/resources/miniSmoke.png");
            }
            catch (error) {
                console.log(error);
            }
            thrusterPSPrimitive.current = createThrusterPS(smoke);
            unsubscribeThrusterPSUpdater.current = addClockEvent(updateThrusterPS);
        };
        if (isViewerLoaded && parentName) {
            unsubscribeThrusterPSUpdater.current &&
                unsubscribeThrusterPSUpdater.current();
            (thrusterEntity === null || thrusterEntity === void 0 ? void 0 : thrusterEntity.current) && removeEntity(thrusterEntity.current);
            (thrusterPSPrimitive === null || thrusterPSPrimitive === void 0 ? void 0 : thrusterPSPrimitive.current) &&
                removePrimitive(thrusterPSPrimitive.current);
            thrusterEntity.current = addThruster();
            readSmokeAndInitialize();
        }
        return () => {
            unsubscribeThrusterPSUpdater.current &&
                unsubscribeThrusterPSUpdater.current();
            (thrusterEntity === null || thrusterEntity === void 0 ? void 0 : thrusterEntity.current) && removeEntity(thrusterEntity.current);
            (thrusterPSPrimitive === null || thrusterPSPrimitive === void 0 ? void 0 : thrusterPSPrimitive.current) &&
                removePrimitive(thrusterPSPrimitive.current);
        };
    }, [isViewerLoaded, id, parentName, positionOffset]);
    return null;
};
//# sourceMappingURL=Thruster.js.map